import { useMemo } from 'react'

import CircleIcon from '@mui/icons-material/Circle'
import _get from 'lodash/get'
import { shallowEqual } from 'react-redux'

import CustomTooltip from '../../../../_components/CustomTooltip'
import { formatIncludeCheckboxEmployeeCount } from '../../../report/securitiesReportIndicators/extra/formatters'
import { useSecuritiesReportDispatch, useSecuritiesReportSelector } from '../extra/store'

const AggregationTargetSetting = () => {
  const aggregationTargetList = useSecuritiesReportSelector(
    (s) => _get(s.pageState, [s.selectedFiscalYear, 'groupCompany'], []),
    shallowEqual
  )

  const stats = useMemo(() => {
    const total = aggregationTargetList.length

    let aggregationTargetCount = 0
    const unRegisteredTargets = []

    for (const target of aggregationTargetList) {
      if (target.employeeCountInfo.aggregationTarget === 'on') aggregationTargetCount++

      if (!target.employeeCountInfo.registrationStatus && target.employeeCountInfo.aggregationTarget === 'on') {
        unRegisteredTargets.push(target.compName)
      }
    }

    return {
      totalAggregationTargets: total || 0,
      aggregationTargetCount,
      unRegisteredTargets,
      unRegisteredTargetsCount: unRegisteredTargets.length
    }
  }, [aggregationTargetList])

  const joinedUnRegistered = `(${stats.unRegisteredTargets.join('、')})`

  return (
    <>
      <h2 className="text-body-2-bold grow">■集計対象設定</h2>
      <p className="mt-5">有価証券報告書の対象とする会社を選択してください。</p>
      <p>
        CHROFY利用ありの会社については、管理職に占める女性労働者の割合等のジェンダー統計情報について、会社ごとの計算・表示要否を設定してください。
      </p>
      <p>CHROFY利用なしの会社のジェンダー統計情報は、登録済(✔)のデータが自動的に表示されます。</p>

      <h2 data-testid="aggregationTargetStats" className="text-body-2-medium my-5">
        集計対象： {stats.aggregationTargetCount}社 / 全{stats.totalAggregationTargets}社
      </h2>

      {stats.unRegisteredTargetsCount > 0 && (
        <div className="p-5 my-3 bg-[#FFF6DD] text-[#C00000] " data-testid="unregistered">
          {stats.unRegisteredTargetsCount}社の従業員数集計データが未登録です。 <br />
          {joinedUnRegistered}
        </div>
      )}

      <div className="setting-table w-full ">
        <table className="base-table table-fixed   w-full">
          <colgroup>
            <col className="w-[150px]" />
            <col className="w-[auto]" />
            <col className="w-[110px]" />
            <col className="w-[112px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
            <col className="w-[100px]" />
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2}>法人番号</th>
              <th rowSpan={2}>会社名</th>
              <th rowSpan={2}>会社種類</th>
              <th rowSpan={2} className="!whitespace-normal text-center text-[13px] leading-[1.35] !px-2">
                <span className="block whitespace-nowrap">提出会社の状況</span>
                <span className="block whitespace-nowrap">
                  に含める<sup>※2</sup>
                </span>
              </th>
              <th colSpan={2}>従業員数情報</th>
              <th colSpan={4}>ジェンダー統計情報</th>
            </tr>
            <tr>
              <th>
                登録状況<sup>※1</sup>
              </th>
              <th>集計対象</th>
              <th>
                登録状況<sup>※1</sup>
              </th>
              <th>管理職比率</th>
              <th>育休取得率</th>
              <th>賃金差異</th>
            </tr>
          </thead>
          <tbody className="text-base">
            {aggregationTargetList.map((target, index) => (
              <CompanyRow target={target} key={target?.searchKey || index} index={index} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-5 text-base" style={{ paddingLeft: '2em', textIndent: '-2em' }}>
        ※1 対象決算期の年次データ登録状況
      </p>
      <p className="mt-1 text-base" style={{ paddingLeft: '2em', textIndent: '-2em' }}>
        ※2
        提出会社が子会社の経営管理を行うことを主たる業務とする会社である場合には、提出会社に加えて最大人員会社についても、従業員数・平均年齢・平均勤続年数・平均年間給与及び平均年間給与の対前事業年度増減率を記載することとなっています。最大人員会社が連結会社の従業員数を超えない場合には、連結子会社のうち、その次に従業員数が多い会社を含みます。
      </p>
    </>
  )
}

const CompanyRow = (props) => {
  const { target, index } = props

  const { subCorporationNumber, isChrofyEnabled, compName, compType } = target

  const { employeeCountInfo, genderStatisticsInfo } = target
  const dispatch = useSecuritiesReportDispatch()

  const isRowDisabled = employeeCountInfo.aggregationTarget !== 'on'

  const _updateGroupCompanyValues = (path, value) => {
    const _value = value ? 'on' : 'off'

    dispatch('SET_GROUP_COMPANY', {
      path: [index, ...path.split('.')],
      value: _value
    })
  }

  /**
   * @function _handleAggregationTargetChange 従業員数情報
   * @param {boolean} value
   */
  const _handleAggregationTargetChange = (value) => {
    _updateGroupCompanyValues('employeeCountInfo.aggregationTarget', value)

    if (!value && isChrofyEnabled) {
      _updateGroupCompanyValues('employeeCountInfo.includeInSubmittingCompanyStatus', false)
      _handleFemaleManagerRatio(false)
      _handleParentalLeaveRate(false)
      _handleWageGap(false)
    }
  }

  /**
   * @function _handleIncludeInSubmittingCompanyStatusChange 提出会社の状況に含める
   * @param {boolean} value
   */
  const _handleIncludeInSubmittingCompanyStatusChange = (value) =>
    _updateGroupCompanyValues('employeeCountInfo.includeInSubmittingCompanyStatus', value)

  /**
   * @function _handleFemaleManagerRatio 女性管理食比率
   * @param {boolean} value
   */
  const _handleFemaleManagerRatio = (value) =>
    _updateGroupCompanyValues('genderStatisticsInfo.femaleManagerRatio', value)

  /**
   * @function _handleParentalLeave 育児休業取得率
   * @param {boolean} value
   */
  const _handleParentalLeaveRate = (value) => _updateGroupCompanyValues('genderStatisticsInfo.parentalLeaveRate', value)
  /**
   * @function _handleWageGap 賃金差異
   * @param {boolean} value
   */

  const _handleWageGap = (value) => _updateGroupCompanyValues('genderStatisticsInfo.wageGap', value)

  const isReportSubmittingCompany = compType === '提出会社'

  const reportSubmittingAggregationTarget = isReportSubmittingCompany ? 'on' : employeeCountInfo.aggregationTarget

  const reportSubmittingIncludeInSubmittingCompanyStatus = isReportSubmittingCompany
    ? 'on'
    : employeeCountInfo.includeInSubmittingCompanyStatus

  const includeCheckboxEmployeeCountLabel = isChrofyEnabled
    ? formatIncludeCheckboxEmployeeCount(employeeCountInfo.regularEmployee)
    : null

  return (
    <tr
      className={`h-[50px] ${isRowDisabled && 'bg-gray-200/50'}`}
      key={subCorporationNumber}
      data-testid={`group-${subCorporationNumber}`}
    >
      <td data-testid="subCorporationNumber">{subCorporationNumber}</td>
      <td data-testid="compName">
        <CustomTooltip title={compName}>
          {(ref) => (
            <div className="truncate" ref={ref}>
              {compName}
              <span className="ml-3">
                {isChrofyEnabled && <CheckIcon data-testid={'isChrofyEnabled'} className="!text-green-500" />}
              </span>
            </div>
          )}
        </CustomTooltip>
      </td>
      <td data-testid="compType">{compType} </td>
      <td data-testid="employeeCountInfo.includeInSubmittingCompanyStatus">
        <div className="flex items-center justify-center gap-1">
          {isChrofyEnabled ? (
            <>
              <AggregationTargetCheckBox
                showCheckbox
                disabled={isReportSubmittingCompany || isRowDisabled}
                state={reportSubmittingIncludeInSubmittingCompanyStatus}
                onChange={_handleIncludeInSubmittingCompanyStatusChange}
              />
              {includeCheckboxEmployeeCountLabel != null && (
                <span data-testid="employeeCountInfo.employeeCountDisplay" className="text-[13px] text-gray-800">
                  {includeCheckboxEmployeeCountLabel}
                </span>
              )}
            </>
          ) : null}
        </div>
      </td>
      <td data-testid="employeeCountInfo.registrationStatus">
        <div className="flex align-center justify-center">
          <AggregationTargetRegisterStatus disabled={isRowDisabled} state={employeeCountInfo.registrationStatus} />
        </div>
      </td>
      <td data-testid="employeeCountInfo.aggregationTarget">
        <div className="flex align-center justify-center">
          <AggregationTargetCheckBox
            showCheckbox
            disabled={isReportSubmittingCompany}
            state={reportSubmittingAggregationTarget}
            onChange={_handleAggregationTargetChange}
          />
        </div>
      </td>
      <td data-testid="genderStatisticsInfo.registrationStatus">
        <div className="flex align-center justify-center">
          <AggregationTargetRegisterStatus disabled={isRowDisabled} state={genderStatisticsInfo.registrationStatus} />
        </div>
      </td>
      <td data-testid="genderStatisticsInfo.femaleManagerRatio">
        <div className="flex align-center justify-center">
          <AggregationTargetCheckBox
            disabled={isRowDisabled}
            showCheckbox={isChrofyEnabled}
            state={genderStatisticsInfo.femaleManagerRatio}
            onChange={_handleFemaleManagerRatio}
          />
        </div>
      </td>
      <td data-testid="genderStatisticsInfo.parentalLeaveRate">
        <div className="flex align-center justify-center">
          <AggregationTargetCheckBox
            disabled={isRowDisabled}
            showCheckbox={isChrofyEnabled}
            state={genderStatisticsInfo.parentalLeaveRate}
            onChange={_handleParentalLeaveRate}
          />
        </div>
      </td>
      <td data-testid="genderStatisticsInfo.wageGap">
        <div className="flex align-center justify-center">
          <AggregationTargetCheckBox
            disabled={isRowDisabled}
            showCheckbox={isChrofyEnabled}
            state={genderStatisticsInfo.wageGap}
            onChange={_handleWageGap}
          />
        </div>
      </td>
    </tr>
  )
}

const AggregationTargetRegisterStatus = (props) => {
  const { state, disabled = false } = props
  const color = !disabled ? '#00B050' : '#808080'

  return state ? <CircleIcon data-testid="circle" className={`text-[${color}]`} fontSize={'16'} /> : null
}

export const CheckIcon = ({ className, ...props }) => (
  <span data-testid={props?.['data-testid']} className={`text-gray-300 ${className} `}>
    ✔
  </span>
)

const AggregationTargetCheckBox = (props) => {
  const { state: _state, disabled = false, showCheckbox = false, onChange = undefined } = props

  const isChecked = _state === 'on'

  if (!showCheckbox) {
    return <>{_state === 'has_data' && !disabled && <CheckIcon data-testid="dataCheck" />}</>
  }

  return (
    <>
      <label className="ui-checkbox justify-center">
        <input
          tabIndex={0}
          type="checkbox"
          data-testid="dataCheckbox"
          className="ui-checkbox__input focus:ring focus:ring-offset-2 focus:ring-red-500 cursor-pointer"
          checked={isChecked}
          disabled={disabled}
          onChange={() => onChange?.(!isChecked)}
        />
        <span className="ui-checkbox__icon"></span>
        <span className="ui-checkbox__ripple"></span>
      </label>
    </>
  )
}

export default AggregationTargetSetting
